import type {
  ShootDayCrewMember,
  ShootDayContact,
  ShootDayFields,
  ShootDayKitItem,
  ShootDayScheduleItem,
  KitSectionName,
} from './shootDaySource'
import { ALL_SECTIONS_ON, type CallSheetSections } from './callSheetSections'

const KIT_SECTIONS: KitSectionName[] = ['Camera', 'Audio', 'Lighting', 'Media/Power']

export interface CallSheetPdfData {
  projectName: string
  projectClient: string
  dateLine: string
  fields: ShootDayFields
  schedule: ShootDayScheduleItem[]
  crew: ShootDayCrewMember[]
  extraCrew: ShootDayContact[]
  clientContact: string
  clientPhone: string
  clientContacts: ShootDayContact[]
  guests: ShootDayContact[]
  kit: ShootDayKitItem[]
  /**
   * Which optional segments this shoot publishes (13 Sep 2026). The caller
   * hands over ALL the data either way and this decides what to print, so a
   * switched-off segment is left off the page without anything being
   * deleted upstream. Optional, defaulting to everything on, so an older
   * caller keeps producing exactly the sheet it always did.
   *
   * Only Kit and Files are affected here. Shots has never appeared on the
   * printed sheet at all (it's a live checklist for the crew link), and the
   * remaining segments have no switch by design — see callSheetSections.ts.
   */
  sections?: CallSheetSections
}

/* ---------------------------------------------------------------------------
 * Page
 *
 * A4, not US Letter. The call sheet is printed on the Portal Productions
 * letterhead, which was drawn for A4 - and A4 is the paper anyone here is
 * actually going to put in a printer. Every measurement below is in points.
 *
 * The top and bottom bounds come straight from the letterhead geometry in
 * assets/letterhead.ts: the header bar ends at 60.02pt and the footer line
 * begins at 813.11pt, so type is kept clear of both. The left measure is the
 * lockup's own left edge, so the page hangs off a single line.
 * ------------------------------------------------------------------------- */
const PAGE_W = 595.28
const PAGE_H = 841.89
const MARGIN = 50
const CONTENT_W = PAGE_W - MARGIN * 2
/** First page starts lower: the title block wants air under the header bar. */
const TOP_FIRST = 112
const TOP_CONT = 96
const BOTTOM = 792

/* The monochrome palette, matching the product's own ink/paper/mute tokens.
   No colour anywhere - not even for links, which are set in ink and underlined
   rather than the usual blue. The letterhead is pure greyscale and the product
   carries no accent colour; a blue hyperlink would be the only colour on the
   page and would read as an accident. */
const INK: [number, number, number] = [11, 11, 10]
const MUTE: [number, number, number] = [139, 135, 125]
const RULE: [number, number, number] = [220, 215, 204]
/** Table heads pick up the tone of the letterhead's own header bar. */
const BAND: [number, number, number] = [58, 58, 56]
const PAPER: [number, number, number] = [244, 241, 234]

type Doc = {
  addPage: () => void
  setPage: (page: number) => void
  getNumberOfPages: () => number
  getCurrentPageInfo: () => { pageNumber: number }
  addImage: (
    data: string,
    format: string,
    x: number,
    y: number,
    width: number,
    height: number,
    alias?: string,
    compression?: string,
  ) => void
  setFont: (family: string, style: string) => void
  setFontSize: (size: number) => void
  setTextColor: (r: number, g: number, b: number) => void
  setDrawColor: (r: number, g: number, b: number) => void
  setFillColor: (r: number, g: number, b: number) => void
  setLineWidth: (width: number) => void
  setProperties: (properties: Record<string, string>) => void
  text: (
    text: string | string[],
    x: number,
    y: number,
    options?: Record<string, unknown>,
  ) => void
  textWithLink: (text: string, x: number, y: number, options: { url: string }) => void
  getTextWidth: (text: string) => number
  splitTextToSize: (text: string, width: number) => string[]
  line: (x1: number, y1: number, x2: number, y2: number) => void
  circle: (x: number, y: number, r: number, style?: string) => void
  rect: (x: number, y: number, width: number, height: number, style?: string) => void
  save: (filename: string) => void
  lastAutoTable?: { finalY: number }
}

/**
 * Builds the call sheet as a real PDF file on the Portal Productions
 * letterhead and triggers a direct download - no browser print dialog. This is
 * the one place the call sheet's printed layout lives; there is no separate
 * print-only view to keep in sync with.
 *
 * jsPDF, its autotable plugin and the letterhead are all imported on demand,
 * so the bundle only pays for them the moment someone actually downloads a
 * call sheet - same pattern as the on-demand mammoth import in utils/docx.ts.
 */
export async function downloadCallSheetPdf(data: CallSheetPdfData): Promise<void> {
  const sections = data.sections ?? ALL_SECTIONS_ON

  const [{ jsPDF }, autoTableImport, letterhead] = await Promise.all([
    import('jspdf'),
    import('jspdf-autotable'),
    import('../assets/letterhead'),
  ])

  const { LETTERHEAD_A4, LETTERHEAD_ARCS, letterheadArcCircles } = letterhead

  const autoTable = ((autoTableImport as { default?: unknown }).default ?? autoTableImport) as (
    doc: unknown,
    options: Record<string, unknown>,
  ) => void

  const doc = new jsPDF({ unit: 'pt', format: 'a4' }) as unknown as Doc
  let y = TOP_FIRST

  /* ---------------------------------------------------------------------
   * Letterhead
   *
   * Every page gets the letterhead laid down BEFORE anything else lands on
   * it, so type sits over the arc watermark rather than under it. Two things
   * make that happen: addPage() below paints as soon as it turns the page,
   * and every autoTable call passes willDrawPage, which autotable fires
   * before it draws anything on a page it has just added.
   *
   * `painted` is what keeps those two from fighting. autotable fires
   * willDrawPage for the FIRST page of a table too - a page that already has
   * a title block and several sections on it - and repainting there would
   * drop the letterhead on top of that content. Recording which page numbers
   * have been painted makes the second call a no-op.
   *
   * The arc motif is stroked rather than placed: it is eight perfect circles
   * (see assets/letterheadArcs.ts), so it costs nothing to carry and prints
   * at the printer's resolution rather than the artwork's. They run off the
   * right and bottom edges by design; the PDF clips them at the page.
   *
   * The two real images are each passed an alias, so jsPDF embeds them once
   * and references them from every page rather than storing a copy per page.
   * A failure there must not cost Tom the call sheet: if a piece of artwork
   * won't draw, the page is still a complete, correct call sheet, which is
   * far better than an exception on the way to a shoot.
   * ------------------------------------------------------------------- */
  const painted = new Set<number>()

  function paintLetterhead(): void {
    const page = doc.getCurrentPageInfo().pageNumber
    if (painted.has(page)) return
    painted.add(page)

    const grey = LETTERHEAD_ARCS.strokeGrey
    doc.setDrawColor(grey, grey, grey)
    doc.setLineWidth(LETTERHEAD_ARCS.strokeWidth)
    letterheadArcCircles().forEach((arc) => {
      doc.circle(arc.x, arc.y, arc.r, 'S')
    })

    LETTERHEAD_A4.images.forEach((image) => {
      try {
        doc.addImage(
          image.data,
          image.format,
          image.x,
          image.y,
          image.width,
          image.height,
          image.alias,
          'FAST',
        )
      } catch (error) {
        console.warn(`[call sheet] letterhead artwork "${image.alias}" could not be drawn`, error)
      }
    })
  }

  function addPage(): void {
    doc.addPage()
    paintLetterhead()
    y = TOP_CONT
  }

  /** Turns the page when `height` more points won't fit above the footer. */
  function ensure(height: number): void {
    if (y + height > BOTTOM) addPage()
  }

  function setType(size: number, style: 'normal' | 'bold', colour: [number, number, number]): void {
    doc.setFont('helvetica', style)
    doc.setFontSize(size)
    doc.setTextColor(colour[0], colour[1], colour[2])
  }

  function hairline(atY: number): void {
    doc.setDrawColor(RULE[0], RULE[1], RULE[2])
    doc.setLineWidth(0.5)
    doc.line(MARGIN, atY, PAGE_W - MARGIN, atY)
  }

  const tableFinalY = (): number => doc.lastAutoTable?.finalY ?? y

  /** Shared autotable configuration, so every table on the sheet matches. */
  function tableOptions(extra: Record<string, unknown>): Record<string, unknown> {
    return {
      margin: { left: MARGIN, right: MARGIN, top: TOP_CONT, bottom: PAGE_H - BOTTOM },
      theme: 'plain',
      styles: {
        font: 'helvetica',
        fontSize: 8.5,
        cellPadding: { top: 5.5, right: 6, bottom: 5.5, left: 6 },
        textColor: INK,
        lineWidth: 0,
        overflow: 'linebreak',
      },
      headStyles: {
        fillColor: BAND,
        textColor: 255,
        fontSize: 7,
        fontStyle: 'bold',
        cellPadding: { top: 6, right: 6, bottom: 6, left: 6 },
      },
      /* Rows are separated by a wash of the brand's paper tone rather than by
         ruled lines - quieter on the page, and easier to follow across five
         columns on a printed sheet held at arm's length on set. */
      alternateRowStyles: { fillColor: PAPER },
      willDrawPage: () => paintLetterhead(),
      ...extra,
    }
  }

  /**
   * A section heading: tracked caps over a hairline.
   *
   * The 112pt reservation is deliberate. A heading is worth nothing at the
   * foot of a page with its table overleaf, so it only stays put if the
   * heading, the table's head row and the first few rows all fit beneath it.
   * (The previous version of this file drew headings at whatever `y` happened
   * to be, which could put one past the bottom of the page entirely.)
   */
  function sectionHeading(title: string): void {
    ensure(112)
    y += 6
    setType(8.5, 'bold', INK)
    doc.text(title.toUpperCase(), MARGIN, y + 8, { charSpace: 1.3 })
    y += 13
    hairline(y)
    y += 15
  }

  /** One label/value row in the summary block at the top of the sheet. */
  function definitionRow(label: string, value: string, url?: string): void {
    const valueX = MARGIN + 118
    const valueW = PAGE_W - MARGIN - valueX

    setType(9.5, 'normal', INK)
    const lines = doc.splitTextToSize(value, valueW)
    const height = lines.length * 12.5 + 13
    ensure(height)

    setType(7, 'bold', MUTE)
    doc.text(label.toUpperCase(), MARGIN, y + 9, { charSpace: 1.1 })

    setType(9.5, 'normal', INK)
    lines.forEach((line, index) => {
      const baseline = y + 9 + index * 12.5
      if (url) {
        doc.textWithLink(line, valueX, baseline, { url })
        /* Underlined in ink rather than set in blue - see the palette note. */
        doc.setDrawColor(INK[0], INK[1], INK[2])
        doc.setLineWidth(0.4)
        doc.line(valueX, baseline + 2, valueX + doc.getTextWidth(line), baseline + 2)
      } else {
        doc.text(line, valueX, baseline)
      }
    })

    y += height
    hairline(y - 5)
  }

  // --- Title block ---------------------------------------------------------
  paintLetterhead()
  doc.setProperties({
    title: `${data.projectName || 'Call Sheet'} - Call Sheet`,
    author: 'Portal Productions',
    subject: 'Call Sheet',
  })

  setType(7.5, 'bold', MUTE)
  doc.text('CALL SHEET', MARGIN, y, { charSpace: 1.6 })
  y += 26

  setType(21, 'normal', INK)
  const titleLines = doc.splitTextToSize(data.projectName || 'Call Sheet', CONTENT_W)
  titleLines.forEach((line, index) => doc.text(line, MARGIN, y + index * 24))
  y += (titleLines.length - 1) * 24

  const subtitle = [data.projectClient, data.dateLine]
    .map((part) => part.trim())
    .filter(Boolean)
    .join('   ·   ')
  if (subtitle) {
    y += 16
    setType(10, 'normal', MUTE)
    doc.text(subtitle, MARGIN, y)
  }

  y += 16
  hairline(y)
  y += 20

  // --- Summary -------------------------------------------------------------
  const cleanSchedule = data.schedule.filter((item) => item.timeLabel.trim() || item.title.trim())

  /* The span of the day, read off the schedule rather than invented: the first
     item's start and the last item's end (falling back to its start when no
     end time was entered). Only shown when the two actually differ, so a
     single-item schedule doesn't print "09:00 - 09:00". */
  const firstItem = cleanSchedule[0]
  const lastItem = cleanSchedule[cleanSchedule.length - 1]
  const dayStart = firstItem ? firstItem.timeLabel.trim() : ''
  const dayEnd = lastItem ? lastItem.endTimeLabel.trim() || lastItem.timeLabel.trim() : ''
  if (dayStart && dayEnd && dayStart !== dayEnd) {
    definitionRow('Schedule', `${dayStart} — ${dayEnd}`)
  }

  if (data.fields.locationAddress.trim()) {
    definitionRow('Location', data.fields.locationAddress.trim())
  }
  if (data.fields.locationMapUrl.trim()) {
    definitionRow('Getting there', 'Open in Google Maps', data.fields.locationMapUrl.trim())
  }
  if (data.fields.weather.trim()) {
    definitionRow('Weather', data.fields.weather.trim())
  }
  if (data.fields.parkingNotes.trim()) {
    definitionRow('Parking', data.fields.parkingNotes.trim())
  }

  // --- Important instructions ----------------------------------------------
  /* Set as a single-cell table rather than a hand-drawn box so that a long
     note paginates by itself instead of running off the foot of the page. */
  if (data.fields.importantInstructions.trim()) {
    y += 14
    autoTable(
      doc,
      tableOptions({
        startY: y,
        head: [['IMPORTANT']],
        body: [[data.fields.importantInstructions.trim()]],
        styles: {
          font: 'helvetica',
          fontSize: 9.5,
          textColor: INK,
          fillColor: PAPER,
          lineWidth: 0,
          overflow: 'linebreak',
          cellPadding: { top: 2, right: 18, bottom: 14, left: 18 },
        },
        headStyles: {
          fillColor: PAPER,
          textColor: MUTE,
          fontSize: 7,
          fontStyle: 'bold',
          cellPadding: { top: 14, right: 18, bottom: 4, left: 18 },
        },
        alternateRowStyles: {},
        /* The ink rule down the left edge, drawn per cell so it survives a
           page break in the middle of a long note. */
        didDrawCell: (cell: { cell: { x: number; y: number; height: number } }) => {
          doc.setFillColor(INK[0], INK[1], INK[2])
          doc.rect(cell.cell.x, cell.cell.y, 2.5, cell.cell.height, 'F')
        },
      }),
    )
    y = tableFinalY() + 10
  }

  // --- Schedule -------------------------------------------------------------
  if (cleanSchedule.length) {
    sectionHeading('Schedule')
    autoTable(
      doc,
      tableOptions({
        startY: y,
        head: [['TIME', 'ITEM', 'WHO', 'WHERE', 'NOTE']],
        body: cleanSchedule.map((item) => [
          item.timeLabel + (item.endTimeLabel ? `–${item.endTimeLabel}` : ''),
          item.title,
          item.peopleNote,
          item.locationNote,
          item.note,
        ]),
        columnStyles: {
          0: { cellWidth: 72 },
          1: { cellWidth: 132 },
          2: { cellWidth: 86 },
          3: { cellWidth: 105 },
        },
      }),
    )
    y = tableFinalY() + 26
  }

  // --- Crew -------------------------------------------------------------
  const crewRows = [
    ...data.crew.map((member) => [member.name, member.role, member.phone || '—']),
    ...data.extraCrew
      .filter((item) => item.name.trim())
      .map((item) => [
        item.name,
        item.role + (item.remote ? ' (Remote)' : ''),
        item.phone || '—',
      ]),
  ]
  if (crewRows.length) {
    sectionHeading('Crew')
    autoTable(
      doc,
      tableOptions({
        startY: y,
        head: [['NAME', 'ROLE', 'PHONE']],
        body: crewRows,
        columnStyles: { 0: { cellWidth: 190 }, 1: { cellWidth: 190 } },
      }),
    )
    y = tableFinalY() + 26
  }

  // --- Client & guests -------------------------------------------------------
  const clientGuestRows: string[][] = []
  if (data.clientContact) {
    clientGuestRows.push([data.clientContact, 'Client Contact', data.clientPhone || '—'])
  }
  data.clientContacts
    .filter((item) => item.name.trim())
    .forEach((item) => clientGuestRows.push([item.name, item.role, item.phone || '—']))
  data.guests
    .filter((item) => item.name.trim())
    .forEach((item) =>
      clientGuestRows.push([
        item.name,
        item.role ? `Guest — ${item.role}` : 'Guest',
        item.phone || '—',
      ]),
    )

  if (clientGuestRows.length) {
    sectionHeading('Client & Guests')
    autoTable(
      doc,
      tableOptions({
        startY: y,
        head: [['NAME', 'ROLE', 'PHONE']],
        body: clientGuestRows,
        columnStyles: { 0: { cellWidth: 190 }, 1: { cellWidth: 190 } },
      }),
    )
    y = tableFinalY() + 26
  }

  // --- Kit -------------------------------------------------------------
  /* Skipped entirely when the Kit segment is switched off for this shoot
     (13 Sep 2026) - a live broadcast doesn't want a kit checklist on the
     printed sheet, even though the list is still sitting in the builder for
     the next studio day. The rows are simply never built, so the heading
     goes with them rather than sitting above an empty table.

     The section name is printed once per group rather than repeated down the
     column - four consecutive rows reading "Camera" is noise, and the blank
     left column reads as the grouping it actually is. */
  const kitRows: string[][] = []
  if (sections.kit) {
    KIT_SECTIONS.forEach((section) => {
      data.kit
        .filter((item) => item.section === section && item.name.trim())
        .forEach((item, index) => {
          kitRows.push([
            index === 0 ? section : '',
            item.name,
            item.quantity || '—',
            item.owner || '—',
          ])
        })
    })
  }
  if (kitRows.length) {
    sectionHeading('Kit')
    autoTable(
      doc,
      tableOptions({
        startY: y,
        head: [['SECTION', 'ITEM', 'QTY', 'OWNER']],
        body: kitRows,
        columnStyles: {
          0: { cellWidth: 110 },
          1: { cellWidth: 235 },
          2: { cellWidth: 60 },
        },
      }),
    )
    y = tableFinalY() + 26
  }

  // --- Files -------------------------------------------------------------
  /* Same treatment as Kit above: switched off for this shoot means the
     links never make it onto the page. Important Instructions is NOT part of
     this switch - it prints above regardless. */
  const files: [string, string][] = sections.files
    ? [
        ['Script', data.fields.scriptUrl],
        ['Guest Bios', data.fields.guestBiosUrl],
        ['Storyboard', data.fields.storyboardUrl],
        ['Dropbox / Drive', data.fields.dropboxUrl],
      ]
    : []
  const presentFiles = files.filter(([, url]) => url && url.trim())
  if (presentFiles.length) {
    sectionHeading('Files')
    presentFiles.forEach(([label, url]) => {
      ensure(16)
      setType(9, 'normal', MUTE)
      const labelWidth = doc.getTextWidth(`${label}  `)
      doc.text(`${label}  `, MARGIN, y + 8)

      setType(9, 'normal', INK)
      const trimmed = url.trim()
      doc.textWithLink(trimmed, MARGIN + labelWidth, y + 8, { url: trimmed })
      doc.setDrawColor(INK[0], INK[1], INK[2])
      doc.setLineWidth(0.4)
      doc.line(
        MARGIN + labelWidth,
        y + 10,
        MARGIN + labelWidth + doc.getTextWidth(trimmed),
        y + 10,
      )
      y += 15
    })
  }

  /* Page numbers last, once the total is known. Set against the right margin
     so they sit on the same line as the letterhead's centred footer without
     crowding it. */
  const pageCount = doc.getNumberOfPages()
  if (pageCount > 1) {
    for (let page = 1; page <= pageCount; page += 1) {
      doc.setPage(page)
      setType(7, 'normal', MUTE)
      doc.text(`${page} / ${pageCount}`, PAGE_W - MARGIN, 822, {
        align: 'right',
        charSpace: 0.8,
      })
    }
  }

  doc.save(`${fileNameSafe(data.projectName)}-call-sheet.pdf`)
}

function fileNameSafe(name: string): string {
  return (
    name
      .trim()
      .replace(/[^a-z0-9]+/gi, '-')
      .replace(/^-+|-+$/g, '')
      .toLowerCase() || 'call-sheet'
  )
}
